import { useQuery } from "@apollo/client/react";
import { Chip, ChipProps, Typography, chipClasses } from "@mui/material";
import { Stack, styled } from "@mui/system";
import {
  DataGridPro,
  GridColDef,
  GridFilterModel,
  GridLogicOperator,
  GridSlotsComponentsProps,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import { ArrowRightIcon } from "@phosphor-icons/react/dist/csr/ArrowRight";
import { KeyIcon } from "@phosphor-icons/react/dist/csr/Key";
import { capitalize } from "humanize-plus";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CREDENTIAL_TYPES } from "../../../components";
import IconCopy from "../../../components/common/IconCopy";
import { useAccountContext } from "../../../strict/app/AccountResolver";
import { CredentialsDocument } from "../../../graphql/types";
import type { CredentialsQuery, CredentialsQueryVariables } from "../../../graphql/types";

import { DatagridEmptyState } from "../../../components/DatagridEmptyState/DatagridEmptyState";
import typography from "../../../styles/sbDesignSystem/sbTypography";
import { UnsuspendKeyButton } from "./UnsuspendKeyButton";

const PAGE_SIZE = 20;

const Container = styled(Stack)({
  width: "100%",
  borderRadius: "8px",
  height: "70vh",
});

const DataGridProStyled = styled(DataGridPro)(({ theme }) => ({
  height: "100%",
  width: "100%",
  // @ts-ignore
  boxShadow: theme.mixins.shadowSurfaceLight10,
  ...theme.applyStyles("dark", {
    // @ts-ignore
    boxShadow: theme.mixins.shadowSurfaceDark10,
  }),
})) as typeof DataGridPro;
const ArrowActionContainer = styled(Stack)({
  overflow: "hidden",
  textOverflow: "ellipsis",
  padding: 0,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "100%",
});

const StatusBadge = styled(Chip)<ChipProps>(({ label, theme }) => ({
  backgroundColor: label === "Active" ? theme.palette.accentSuccess20.main : theme.palette.accentDanger20.main,

  [`& .${chipClasses.label}`]: {
    color: theme.palette.text50.main,
    ...typography?.["button.xSmall"],
  },
})) as typeof Chip;

const EmptyRows = ({ isFiltering }: NonNullable<GridSlotsComponentsProps["noRowsOverlay"]>) => {
  return (
    <DatagridEmptyState Icon={KeyIcon} text={isFiltering ? "No results, try another filter" : "You have no keys yet"} />
  );
};

type KeyListProps = {
  search: string;
  statusFilters: string[];
};

const keyDetailsRedirect = (keyName: string, urlName: string) =>
  `/app/${urlName}/keys/details?keyName=${encodeURIComponent(keyName)}`;

const KeyList = ({ search, statusFilters }: KeyListProps) => {
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: "name", sort: "asc" }]);

  const { loading, data, fetchMore, refetch } = useQuery<CredentialsQuery, CredentialsQueryVariables>(
    CredentialsDocument,
    {
      variables: {
        first: PAGE_SIZE,
        search: search || undefined,
        statusFilters: statusFilters.length > 0 ? statusFilters : undefined,
        sortBy: sortModel[0]?.field || undefined,
        sortDirection: sortModel[0]?.sort || undefined,
      },
      errorPolicy: "all",
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first",
    }
  );

  const { urlName } = useAccountContext();

  const navigate = useNavigate();
  const isLoadingMoreRef = useRef(false);

  const onSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
  };

  useEffect(() => {
    const currentSortBy = sortModel[0]?.field || undefined;
    const currentSortDirection = sortModel[0]?.sort || undefined;
    refetch({
      first: PAGE_SIZE,
      search: search || undefined,
      statusFilters: statusFilters.length > 0 ? statusFilters : undefined,
      sortBy: currentSortBy || undefined,
      sortDirection: currentSortDirection || undefined,
    });
  }, [search, statusFilters, sortModel, refetch]);

  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const gridElement = gridRef.current;
    if (!gridElement) return;

    const virtualScroller = gridElement.querySelector(".MuiDataGrid-virtualScroller");
    if (!virtualScroller) return;

    const handleScroll = () => {
      if (isLoadingMoreRef.current || loading) return;

      const scrollTop = virtualScroller.scrollTop;
      const scrollHeight = virtualScroller.scrollHeight;
      const clientHeight = virtualScroller.clientHeight;
      if (scrollHeight - scrollTop - clientHeight < 50) {
        if (data?.credentials?.pageInfo.hasNextPage) {
          isLoadingMoreRef.current = true;
          fetchMore({
            variables: {
              first: PAGE_SIZE,
              after: data?.credentials?.pageInfo.endCursor || undefined,
              search: search || undefined,
              statusFilters: statusFilters.length > 0 ? statusFilters : undefined,
            },
          }).finally(() => {
            isLoadingMoreRef.current = false;
          });
        }
      }
    };

    virtualScroller.addEventListener("scroll", handleScroll);
    return () => {
      virtualScroller.removeEventListener("scroll", handleScroll);
    };
  }, [data?.credentials?.pageInfo, fetchMore, search, statusFilters, loading]);

  const filterModel: GridFilterModel = {
    items: [],
    logicOperator: GridLogicOperator.Or,
  };

  const rows = data?.credentials?.edges.map((edge) => edge.node) || [];
  const columns: GridColDef<(typeof rows)[0]>[] = [
    {
      field: "credential_type",
      headerName: "Type",
      flex: 1,
      editable: false,
      sortable: true,
      sortingOrder: ["desc", "asc"],
      width: 2,
      filterOperators: [
        {
          label: "contains",
          value: "contains",
          getApplyFilterFn: (filterItem) => {
            return (_, row) => {
              const transformedValue =
                CREDENTIAL_TYPES[row.credential_type as keyof typeof CREDENTIAL_TYPES]?.display_name || "";
              return transformedValue.toLocaleLowerCase().includes(filterItem.value.toLocaleLowerCase());
            };
          },
        },
      ],
      renderCell: (params) => (
        <Typography variant="body.small.regular">
          {CREDENTIAL_TYPES[params.row.credential_type as keyof typeof CREDENTIAL_TYPES]?.display_name}
        </Typography>
      ),
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1.5,
      editable: false,
      sortingOrder: ["desc", "asc"],
      sortable: true,
      width: 250,
      renderCell: (params) => (
        <Stack direction="row" sx={{ alignItems: "center", height: "100%", justifyContent: "space-between" }}>
          <Typography
            variant="body.small.semibold"
            sx={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {params.row.name}
          </Typography>
          <div className="actionsGroup">
            <IconCopy clipboard={params.row.name} size={20} />
          </div>
        </Stack>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      editable: false,
      sortable: true,
      width: 120,
      sortingOrder: ["desc", "asc"],
      valueFormatter: (_, row) => capitalize(row.status),
      renderCell: (params) => {
        return <StatusBadge size="small" label={capitalize(params.row.status)} />;
      },
    },
    {
      field: "description",
      headerName: "Description",
      flex: 3,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        return (
          <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", height: "100%" }}>
            <Typography
              variant="body.small.regular"
              sx={{
                width: "100%",
                display: "block",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {params.row?.description ?? "-"}
            </Typography>
            <div className="actionsGroup">
              {params.row?.description && <IconCopy clipboard={params.row.name} size={20} />}
              <UnsuspendKeyButton
                credential={params.row}
                urlName={urlName || ""}
                buttonSize="xSmall"
                sx={{ marginLeft: "16px" }}
              />
            </div>
          </Stack>
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      width: 40,
      resizable: false,
      sortable: false,
      hideSortIcons: true,
      renderCell: () => (
        <ArrowActionContainer>
          <ArrowRightIcon size={20} />
        </ArrowActionContainer>
      ),
    },
  ];

  const isFilterAppliedToExistingRows = (search || statusFilters.length) && rows.length;
  return (
    <Container>
      <DataGridProStyled
        rows={rows}
        sx={{
          ".actionsGroup": {
            display: "none",
          },
          ".MuiDataGrid-row:hover .actionsGroup": {
            opacity: 1,
            alignItems: "center",
            display: "flex",
          },
          ".MuiDataGrid-overlayWrapper": {
            height: isFilterAppliedToExistingRows ? "120px" : undefined,
          },
        }}
        onRowClick={(params) => navigate(keyDetailsRedirect(params.row.name, urlName || ""))}
        filterModel={filterModel}
        columns={columns}
        loading={loading}
        rowHeight={60}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        disableDensitySelector
        isCellEditable={() => false}
        disableRowSelectionOnClick
        disableColumnMenu
        disableColumnFilter
        getRowId={(row) => row.name}
        slots={{
          noRowsOverlay: EmptyRows,
          noResultsOverlay: EmptyRows,
        }}
        hideFooter={true}
        getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "Mui-even" : "Mui-odd")}
        ref={gridRef}
        slotProps={{
          root: {
            "data-testid": "key-list",
          },
          noResultsOverlay: {
            isFiltering: true,
          },
          row: {
            style: {
              cursor: "pointer",
            },
          },
        }}
      />
    </Container>
  );
};

export default KeyList;
